export const en = {
  common: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    add: "Add",
    close: "Close",
    confirm: "Confirm",
    test: "Test",
    testing: "Testing…",
    none: "None",
    loading: "Loading…",
    name: "Name",
    email: "Email",
    password: "Password",
    role: "Role",
    actions: "Actions",
    copy: "Copy",
    copied: "Copied",
    optional: "optional",
    retry: "Retry",
    refresh: "Refresh",
    yes: "Yes",
    no: "No",
    back: "Back",
    saved: "Saved",
    deleted: "Deleted",
    error: "Something went wrong",
    areYouSure: "Are you sure?",
    irreversible: "This action cannot be undone.",
    createdAt: "Created",
  },
  auth: {
    forgotPassword: "Forgot password?",
    forgotTitle: "Reset your password",
    forgotSubtitle: "Enter your email: we will send you a link to choose a new password.",
    forgotSubmit: "Send the link",
    forgotSent: "If an account with a password exists for {{email}}, a link to reset it is on its way. Check your inbox.",
    resetTitle: "Choose a new password",
    resetSubtitle: "Your other sessions will be signed out.",
    resetSubmit: "Save the password",
    confirmPassword: "Confirm the password",
    passwordMismatch: "The passwords do not match",
    passwordResetDone: "Your password has been changed: sign in with it.",
    linkInvalid: "This link is invalid or has expired.",
    requestNewLink: "Request a new link",
    backToLogin: "Back to sign in",
    checkInboxTitle: "Check your inbox",
    checkInboxHelp: "We sent a confirmation link to {{email}}. Open it to activate your account, then sign in.",
    resendVerification: "Send the link again",
    verificationResent: "A new link is on its way.",
    emailVerified: "Your email is confirmed: you can sign in.",
    ssoContinue: "Continue with SSO",
    ssoTitle: "Single sign-on",
    ssoSubtitle: "Enter your work email: you continue on your organization's identity provider.",
    ssoSubmit: "Continue",
    ssoBack: "Sign in with a password",
    ssoNoProvider: "Single sign-on is not set up for this email domain.",
    ssoNotLinked: "An account already exists with this email and cannot be linked to single sign-on. Ask an administrator.",
    ssoFailed: "Single sign-on failed. Try again or ask an administrator.",
    setupTitle: "Welcome to Releo",
    setupSubtitle: "Create the administrator account of this instance.",
    setupSubmit: "Create administrator",
    loginTitle: "Sign in",
    loginSubmit: "Sign in",
    signupTitle: "Create an account",
    signupSubmit: "Create account",
    noAccount: "No account yet?",
    haveAccount: "Already have an account?",
    acceptPrefix: "By creating an account, you accept",
    acceptTerms: "the terms of service",
    acceptAnd: "and",
    acceptPrivacy: "the privacy policy",
    signup: "Sign up",
    login: "Sign in",
    logout: "Sign out",
    passwordHint: "At least 10 characters",
  },
  nav: {
    audit: "Audit log",
    billing: "Billing",
    license: "License",
    organization: "Organization",
    apps: "Apps",
    administration: "Administration",
    users: "Users",
    credentials: "Store credentials",
    llm: "LLM providers",
    git: "Git providers",
    tags: "Tags",
    devices: "Devices",
    instance: "Instance",
    settings: "My settings",
    apiDocs: "API docs",
    docs: "Documentation",
    theme: "Toggle theme",
    language: "Language",
  },
  apps: {
    title: "Apps",
    empty: "No apps yet.",
    emptyUser: "You don't have access to any app yet. Ask an administrator.",
    new: "New app",
    settings: "Settings",
    builds: "Builds",
    all: "All",
    noBuilds: "No builds yet. Drop an .ipa or .aab file above to get started.",
    roleViewer: "Viewer",
    roleManager: "Manager",
    app: "App",
    identifiers: "Identifiers",
    viewGrid: "Grid",
    viewList: "List",
    recentBuilds: "Latest builds",
    noRecentBuilds: "No builds yet.",
    clearFilter: "Clear filter",
    noMatch: "No app has this tag.",
  },
  tags: {
    title: "Tags",
    help: "Shared by every app. App managers can create tags and apply them to their apps; only administrators can rename or delete them.",
    tag: "Tag",
    apps: "Apps",
    color: "Color",
    preview: "Preview:",
    namePlaceholder: "e.g. Client A",
    edit: "Tags",
    editTitle: "Tags of {{name}}",
    editHelp: "Click a tag to add it to or remove it from this app.",
    new: "New tag",
    create: "Create tag",
    noneYet: "No tags yet.",
  },
  publish: {
    noCertificate: "iOS builds on this page cannot be installed yet: the App Store Connect key of the app has no distribution certificate. An administrator can add it in Store credentials.",
    noAppleKey: "iOS builds on this page cannot be installed yet: no App Store Connect key is configured for the app.",
    iosNotInstallable: "iOS build listed, but not installable yet",
    button: "Public page",
    appTitle: "Public page of the app",
    appHelp: "A page reachable without an account through a secret link. It lists the builds you mark as public (eye icon in the builds list); testers download the APK or install the iOS build on their registered iPhone.",
    buildTitle: "Public page of {{version}}",
    buildHelp: "A page reachable without an account through a secret link, to download this build (APK) or install it on a registered iPhone.",
    publish: "Publish",
    published: "Page published",
    link: "Secret link",
    open: "Open",
    approval: "New iPhones (UDID)",
    approvalHelp: "iOS builds are re-signed for each registered iPhone (ad hoc). Apple allows 100 iPhones per year per developer account.",
    approval_admin: "Wait for an administrator's approval",
    approval_auto: "Add them automatically",
    expires: "Expiration",
    expiresHelp: "Optional: the link stops working after this date.",
    expiresOn: "Expires on {{date}}",
    noExpiry: "No expiration",
    autoPublish: "List new builds automatically",
    autoPublishHelp: "Builds uploaded from now on appear on this page right away. Existing builds are not changed.",
    pickBuildsAuto: "New builds are listed automatically. Hide or list a build with the eye icon in the builds list.",
    pickBuilds: "Mark the builds shown on this page with the eye icon in the builds list.",
    regenerate: "New link",
    regenerateHelp: "The current link stops working immediately. Share the new one.",
    unpublish: "Unpublish",
    unpublishHelp: "The link stops working. Registered iPhones are kept.",
    unpublished: "Page unpublished",
    buildPublic: "Listed on the public page — click to hide",
    buildHidden: "Not on the public page — click to list",
  },
  cli: {
    title: "Sign in the Releo CLI",
    subtitle: "Check that the code matches the one shown in your terminal.",
    signInSubtitle: "Sign in to authorize the command line tool.",
    signIn: "Sign in to continue",
    code: "Code",
    organization: "Organization",
    warning: "Only authorize if you just ran `releo login` yourself. The CLI gets an API key with your permissions in this organization.",
    approve: "Authorize",
    deny: "Deny",
    expired: "This code is expired or unknown. Run `releo login` again.",
    used: "This code was already used. Run `releo login` again.",
    approvedTitle: "CLI authorized",
    deniedTitle: "Login denied",
    backToTerminal: "You can close this page and go back to your terminal.",
  },
  invitation: {
    title: "You are invited",
    subtitle: "{{inviter}} invites you to join {{organization}} as {{role}}.",
    subtitleNoInviter: "Join {{organization}} as {{role}}.",
    notFound: "This invitation does not exist.",
    accepted: "This invitation has already been used.",
    revoked: "This invitation was revoked. Ask for a new one.",
    expired: "This invitation has expired. Ask for a new one.",
    join: "Join {{organization}}",
    wrongAccount: "This invitation is for {{email}}, but you are signed in as {{current}}. Sign out, then open the link again.",
    signInToJoin: "Sign in to join",
    createAndJoin: "Create my account and join",
  },
  public: {
    support: "Support",
    poweredBy: "Powered by Releo",
    notFound: "This page does not exist or has expired",
    notFoundHelp: "Ask the person who shared the link for a new one.",
    expires: "Available until {{date}}",
    noBuilds: "No build available yet.",
    buildNotFound: "This build is not available",
    allBuilds: "All builds",
    whatToTest: "What to test",
    openOnIphone: "Open this page on your iPhone",
    openOnAndroid: "Open this page on your Android phone",
    scanHelp: "Scan the QR code with the camera, or send yourself the link.",
    preparing: "Preparing the build…",
    preparingHelp: "This usually takes one or two minutes. The page updates by itself.",
    failed: "The build could not be prepared",
    unavailable: "Not available",
    apkReady: "Ready to install",
    downloadApk: "Download the APK",
    apkHelp: "Open the downloaded file to install it. Android may ask you to allow installing apps from your browser.",
    registerTitle: "Register this iPhone first",
    registerHelp: "iOS test builds only install on registered devices. Registration shares this iPhone's identifier (UDID); nothing is installed.",
    registerButton: "Register this iPhone",
    registerStep1: "Allow the download of the configuration profile.",
    registerStep2: "Open Settings → Profile Downloaded → Install.",
    registerStep3: "Safari opens this page again automatically.",
    useSafari: "Open this page in Safari: other browsers cannot register the iPhone or install the app.",
    pendingTitle: "{{name}} is waiting for approval",
    pendingHelp: "An administrator must approve this iPhone. Keep this page open or come back later: it updates by itself.",
    rejected: "This iPhone was not approved.",
    iosReady: "Ready to install on {{name}}",
    install: "Install",
    installHelp: "Confirm the installation, then find the app on the home screen (installation continues in the background).",
  },
  devices: {
    title: "Devices",
    help: "iPhones registered from public pages. Approved devices are added to the Apple developer account and to the ad hoc profiles (100 iPhones per year per account).",
    status: "Status",
    status_pending: "Pending",
    status_approved: "Approved",
    status_rejected: "Rejected",
    registeredFrom: "Registered from",
    registeredWith: "Apple: {{names}}",
    approve: "Approve",
    reject: "Reject",
    certificate: "Distribution certificate",
    noCertificate: "None",
    addCertificate: "Add",
    replace: "Replace",
    certExpires: "expires {{date}}",
    certificateTitle: "Distribution certificate (.p12)",
    certificateHelp: "Export the Apple Distribution certificate *with its private key* from Keychain Access (.p12). It re-signs iOS builds for the iPhones registered from public pages. Encrypted, never displayed again.",
    p12: ".p12 file",
  },
  search: {
    title: "Search",
    trigger: "Search…",
    placeholder: "Search apps, tags, build numbers, actions…",
    noResults: "No results.",
    apps: "Apps",
    tags: "Tags",
    builds: "Builds",
    actions: "Actions",
    appsWithTag_one: "{{count}} app",
    appsWithTag_other: "{{count}} apps",
    goApps: "All apps",
    darkTheme: "Switch to dark theme",
    lightTheme: "Switch to light theme",
    language: "Passer en français",
  },
  upload: {
    drop: "Drop an .ipa or .aab here, or click to browse",
    dropHint: "The bundle identifier / package name must match this app.",
    uploading: "Uploading {{name}}…",
    done: "Build uploaded, pushing to the store…",
    invalidType: "Only .ipa and .aab files are supported",
    commit: "Commit",
    commitHelp: "Commit the binary was built from, used to write the release notes.",
    notes: "Tester notes",
    notesPlaceholder: "What should testers focus on?",
    prefillAuto: "Generate the store release notes (automatic mode)",
    prefillCopy: "Copy the notes of the previous release",
    options: "Options",
  },
  build: {
    fill: "Fill…",
    fillCommits: "AI + commits",
    fillCommitsHint: "Write the notes from the commits of this build",
    fillTranslate: "Translate with AI",
    fillTranslateHint: "Translate one locale into the others",
    fillCopy: "Copy the previous release",
    fillCopyHint: "Store notes of the previous build, every locale",
    fillNeedsLlm: "Configure an LLM provider in the app settings",
    fillNeedsLlmGit: "Configure an LLM and a git repository in the app settings",
    copied: "Notes copied from the previous release",
    translated: "{{count}} locale(s) translated",
    translateHelp: "The source text is translated into the selected locales (unsaved edits included).",
    translate: "Translate",
    translateNothing: "Write the notes in one locale first.",
    sourceLocale: "Source locale",
    targetLocales: "Translate into",
    willOverwrite: "(will be replaced)",
    source: { llm: "AI", template: "template", manual: "manual", copied: "copied" },
    version: "Version",
    buildNumber: "Build",
    status: "Status",
    uploadedBy: "Uploaded by",
    uploadedAt: "Uploaded",
    size: "Size",
    commit: "Commit",
    track: "Track",
    timeline: "Timeline",
    notes: "Release notes",
    promote: "Promote to production",
    promoteIos: "Submit for App Store review",
    promoteIosHelp: "Creates the App Store version {{version}}, attaches this build, sets “What's New” from the store notes and submits it for review.",
    promoteAndroidHelp: "Promotes the release from the {{from}} track.",
    releaseMode: "Release after approval",
    releaseModeManual: "Manually (from Releo)",
    releaseModeAuto: "Automatically",
    targetTrack: "Target track",
    rollout: "Rollout (%)",
    release: "Release now",
    releaseHelp: "Apple approved this version. Release it to the App Store now?",
    retry: "Retry",
    delete: "Delete build",
    deleteHelp: "Removes the build from Releo only (not from the stores).",
    fileDeleted: "Binary deleted (retention)",
    error: "Error",
    notFound: "Build not found",
    noEvents: "No events yet.",
    details: "Details",
    testingNotes: "Testers",
    storeNotes: "Store",
    testingNotesHelp: "“What to Test” on TestFlight / release notes on the internal track.",
    storeNotesHelp: "“What's New” on the App Store / release notes on Google Play production.",
    generate: "Generate with AI",
    generating: "Generating…",
    generated: "Release notes generated from {{count}} commits",
    saveNotes: "Save notes",
    chars: "{{count}} / {{max}} characters",
    generateMode: "Source",
    modeCommits: "Commits",
    modeTemplate: "Template",
    headCommit: "Build commit",
    headCommitHelp: "No commit was given at upload: pick the one this build was made from.",
    headCommitHelpSet: "Commit given at upload. Change it only if it was wrong.",
    baseFrom: "Base: build {{version}} ({{build}}), commit {{sha}}.",
    baseNone: "No previous release: the most recent commits of the build are used.",
    baseExplain: "Only commits reachable from the build commit and not from the base are listed, whatever the branch.",
    baseCommitHelp: "Override the base commit (e.g. to include more history).",
    sync: {
      testflight: "Changes are pushed to TestFlight (“What to Test”) when you save.",
      play: "Changes are pushed to the {{track}} track release notes when you save.",
      locked: "“What's New” can no longer be changed once the version is submitted to Apple.",
      promotion: "These notes are applied when the build is promoted.",
    },
    baseCommit: "Base commit",
    instructions: "Extra instructions",
    promoted: "Promotion started",
    submittedHelp: "Sent to Google Play. Google does not tell Releo when it is reviewed, approved or live: the Play Console shows it.",
    promotedAt: "Promoted",
  },
  release: {
    open: "Release iOS + Android",
    title: "Release on both stores",
    description: "Promote an iOS and an Android build together, with release notes written once for both stores.",
    submit: "Release on both stores",
    stepBuilds: "Builds",
    stepNotes: "Release notes (both stores)",
    stepOptions: "Options",
    noCandidate: "No build ready to promote",
    versionMismatch: "Different versions: iOS {{ios}} / Android {{android}}.",
    candidatesHelp: "Builds in testing (processed by Apple / on a Play testing track). The most recent pair with the same version is preselected.",
    commitHelp: "Commits since the last release up to this commit are used by “AI + commits”.",
    notesHelp: "The same notes are sent to both stores and replace the current store notes of the two builds. Google Play allows {{max}} characters.",
    noPrevious: "No previous build has store release notes",
    copiedFrom: "Notes copied from {{version}} ({{build}})",
    started: "Release started on both stores",
  },
  status: {
    uploaded: "Uploaded",
    pushing: "Pushing",
    processing: "Processing",
    testing: "In testing",
    promoting: "Promoting",
    in_review: "In review",
    pending_release: "Pending release",
    submitted: "Submitted",
    released: "Released",
    rejected: "Rejected",
    failed: "Failed",
    superseded: "Superseded",
  },
  appSettings: {
    notesFill: "Release notes for each new build",
    notesFillHelp: "How the store release notes are prefilled when a build is uploaded. They can always be edited before promoting.",
    mode_auto: "Automatic (AI + commits)",
    mode_auto_help: "Generated from the commits since the last release, in every locale. Needs an LLM and a git repository.",
    mode_copy_previous: "Copy the previous release",
    mode_copy_previous_help: "The notes of the previous build are copied in every locale. Write the first build manually, the next ones follow. Nothing to configure.",
    mode_manual: "Manual",
    mode_manual_help: "Nothing is prefilled. On each build: write the notes, generate them from the commits, or translate one locale with AI.",
    autoMissing: "Automatic mode needs: {{tools}}.",
    useDefaultTemplate: "Use the default template",
    toolsTitle: "Generation tools (AI & git)",
    toolsHelp: "Used by the automatic mode and by the on-demand actions on each build (generate from commits, translate). Optional in the other modes.",
    llmProviderHelp: "Writes and translates the notes.",
    gitProviderHelp: "Lists the commits between two builds.",
    title: "App settings",
    general: "General",
    ios: "iOS / App Store",
    android: "Android / Google Play",
    members: "Members",
    bundleId: "Bundle identifier",
    ascAppId: "App Store Connect app ID",
    ascAppIdHelp: "Numeric ID (App Store Connect → App Information → Apple ID).",
    appleCredential: "App Store Connect API key",
    testflightGroups: "TestFlight groups",
    testflightGroupsHelp: "Internal groups with access to all builds receive every build automatically. External groups are submitted to Beta App Review.",
    releaseMode: "Release after approval",
    package: "Package name",
    googleCredential: "Service account",
    rollout: "Default production rollout (%)",
    llmProvider: "LLM provider",
    gitProvider: "Git provider",
    gitRepo: "Repository",
    gitRepoHelp: "owner/repo (GitHub, Gitea, Bitbucket), group/project (GitLab) or project/repo (Azure DevOps).",
    locales: "Locales",
    localesHelp: "Store languages of the release notes (App Store codes, converted automatically for Google Play).",
    platforms: "Platforms",
    platformsHelp: "Enable at least one platform. Both are not required: an app can be iOS only or Android only.",
    iosToggleHelp: "Upload .ipa to TestFlight, submit to App Review",
    androidToggleHelp: "Upload .aab to the internal track, promote to production",
    packageHelp: "applicationId of the app. It must already exist in Play Console (first build uploaded manually).",
    testflightGroupsHelpSave: "Group names or IDs. Save the App Store Connect app ID and API key to pick them from the list.",
    groupInternalAuto: "internal · automatic",
    groupInternal: "internal",
    groupExternal: "external · Beta review",
    repoDefaultBranch: "Repository default branch",
    gitBranch: "Default branch",
    gitBranchHelp: "Pre-selected when picking the commit of a build.",
    gitBranchHelpSave: "Save the repository to pick the branch from the list.",
    comparisonHelp: "Release notes cover the commits between the last build that reached the store (same platform) and the build's commit. Branches only matter to pick that commit: CI should send the exact SHA it built.",
    template: "Template",
    templateHelp: "Optional. The rendered text is then adapted and translated by the AI. Handlebars variables: appName, version, buildNumber, platform, date, commits[] (subject, body, shortSha, author). Empty = the AI writes directly from the commits.",
    instructions: "LLM instructions",
    instructionsHelp: "Tone, words to avoid, product vocabulary…",
    deleteApp: "Delete app",
    deleteAppHelp: "Deletes the app, all its builds and binaries from Releo.",
    memberAdd: "Add member",
    noMembers: "Only administrators can access this app.",
    created: "App created",
  },
  audit: {
    title: "Audit log",
    help: "Who did what, and when: every change and sign-in is recorded.",
    orgTitle: "Organization activity",
    instanceTitle: "Instance activity (accounts, license, sign-ins)",
    tabOrganization: "Organization",
    tabInstance: "Instance",
    retention: "Your plan shows the last {{count}} days.",
    export: "Export CSV",
    category: "Category",
    allActions: "All actions",
    actor: "Who",
    everyone: "Everyone",
    from: "From",
    to: "To",
    empty: "No activity matches these filters.",
    date: "Date",
    action: "Action",
    target: "Target",
    details: "Details",
    loadMore: "Load more",
    system: "System",
    deletedUser: "deleted user",
    requiresEnterprise: "Who did what and when, with filters and CSV export: requires Releo Enterprise.",
    requiresPlan: "Who did what and when: available with the Pro (30 days) and Business (1 year, export) plans.",
    categories: {
      build: "Builds",
      release: "Joint releases",
      app: "Apps",
      keystore: "Android keystore",
      publication: "Public pages",
      tag: "Tags",
      organization: "Organization & members",
      apple_credential: "App Store Connect keys",
      google_credential: "Google Play accounts",
      llm_provider: "LLM providers",
      git_provider: "Git providers",
      device: "Devices",
      sso_provider: "Single sign-on",
      branding: "White label",
      billing: "Billing",
      audit: "Audit log",
      auth: "Sign-ins",
      user: "Users",
      license: "License",
      settings: "Instance settings",
      instance: "Instance",
    },
    actions: {
      auth_login: "Signed in",
      auth_login_failed: "Failed sign-in",
      auth_logout: "Signed out",
      auth_password_reset: "Password reset by email",
      organization_invitation_create: "Invited someone",
      organization_invitation_revoke: "Revoked an invitation",
      organization_invitation_accept: "Accepted an invitation",
      instance_setup: "Set up the instance",
      instance_audit_export: "Exported the instance audit log",
      user_signup: "Signed up",
      user_create: "Created a user",
      user_update: "Updated a user",
      user_delete: "Deleted a user",
      settings_update: "Changed instance settings",
      license_install: "Installed a license",
      license_remove: "Removed the license",
      organization_create: "Created the organization",
      organization_update: "Renamed the organization",
      organization_member_add: "Added a member",
      organization_member_update: "Changed a member's role",
      organization_member_remove: "Removed a member",
      api_key_create: "Created an API key",
      api_key_revoke: "Revoked an API key",
      app_create: "Created an app",
      app_update: "Updated an app",
      app_delete: "Deleted an app",
      app_members: "Changed app members",
      app_tags: "Changed app tags",
      build_upload: "Uploaded a build",
      build_retry: "Retried a build",
      build_promote: "Promoted a build",
      build_release: "Released a build",
      build_delete: "Deleted a build",
      build_public: "Changed a build's public listing",
      release_joint: "Released iOS and Android together",
      keystore_upload: "Uploaded the Android keystore",
      keystore_download: "Downloaded the Android keystore",
      keystore_delete: "Deleted the Android keystore",
      publication_create: "Published a public page",
      publication_update: "Updated a public page",
      publication_delete: "Removed a public page",
      tag_create: "Created a tag",
      tag_update: "Updated a tag",
      tag_delete: "Deleted a tag",
      apple_credential_create: "Added an App Store Connect key",
      apple_credential_update: "Updated an App Store Connect key",
      apple_credential_delete: "Deleted an App Store Connect key",
      apple_credential_distribution_certificate: "Stored a distribution certificate",
      apple_credential_distribution_certificate_delete: "Removed a distribution certificate",
      google_credential_create: "Added a Google Play service account",
      google_credential_update: "Updated a Google Play service account",
      google_credential_delete: "Deleted a Google Play service account",
      llm_provider_create: "Added an LLM provider",
      llm_provider_update: "Updated an LLM provider",
      llm_provider_delete: "Deleted an LLM provider",
      git_provider_create: "Added a git provider",
      git_provider_update: "Updated a git provider",
      git_provider_delete: "Deleted a git provider",
      device_update: "Updated a device",
      device_delete: "Deleted a device",
      sso_provider_create: "Added an identity provider",
      sso_provider_update: "Changed SSO enforcement",
      sso_provider_delete: "Removed an identity provider",
      sso_provider_verify_domain: "Verified an SSO domain",
      branding_update: "Changed the white label",
      branding_logo: "Uploaded the logo",
      branding_logo_delete: "Removed the logo",
      billing_checkout: "Started a subscription",
      audit_export: "Exported the audit log",
    },
  },
  billing: {
    title: "Billing",
    help: "Plan, usage and subscription of the organization.",
    freeHelp: "Free plan: upgrade for more apps, builds and editors.",
    renewsOn: "Renews on {{date}}",
    endsOn: "Ends on {{date}}: you return to the free plan, your data is kept.",
    pastDue: "Payment failed",
    manage: "Manage subscription",
    ownersOnly: "Only owners can change the subscription.",
    seePlans: "See plans",
    current: "Current plan",
    unlimited: "unlimited",
    unlimitedUsage: "{{used}} (unlimited)",
    seatsUsage: "Editors",
    appsUsage: "Apps",
    buildsUsage: "Builds this month",
    storageUsage: "Build storage",
    seatsIncluded: "{{count}} editors included",
    seatsPaid: "Billed per editor",
    apps: "Apps: {{value}}",
    builds: "Builds per month: {{value}}",
    storage: "Storage: {{value}}",
    pricePerSeatMonth: "{{price}} per seat per month",
    pricePerSeatYear: "{{price}} per seat per year",
    subscribeMonthly: "{{plan}}, monthly",
    subscribeYearly: "{{plan}}, yearly",
    checkoutSuccess: "Thanks! Your subscription is being activated.",
    seatsExplained: "Editors are owners, administrators and app managers. Viewers are free and unlimited. Seats follow your members automatically (prorated).",
    tagline: { free: "To try Releo with a small team.", pro: "For teams shipping several apps.", business: "Single sign-on and controls for companies." },
  },
  branding: {
    title: "White label",
    help: "Your name, logo, color and support contact on the public pages (secret links) and the iPhone registration profile.",
    requiresEnterprise: "Public pages with your own name, logo and color, without “Powered by Releo”: requires Releo Enterprise.",
    displayName: "Name shown",
    color: "Main color",
    colorPicker: "Pick the main color",
    supportUrl: "Support link",
    supportEmail: "Support email",
    logo: "Logo",
    logoHelp: "PNG, JPEG or WebP, up to 256 KB.",
    logoTooLarge: "The logo must be at most 256 KB",
    removeLogo: "Remove",
    hidePoweredBy: "Hide “Powered by Releo”",
  },
  sso: {
    title: "Single sign-on",
    help: "People with an email of the domain sign in through your identity provider and join the organization as members.",
    requiresEnterprise: "Sign in with your identity provider (OpenID Connect or SAML): requires Releo Enterprise.",
    add: "Add an identity provider",
    protocol: "Protocol",
    domain: "Email domain",
    domainHelp: "Several domains: comma-separated (acme.com,acme.fr).",
    issuer: "Issuer URL",
    issuerHelp: "Its discovery document is read from /.well-known/openid-configuration.",
    clientId: "Client ID",
    clientSecret: "Client secret",
    entityId: "Identity provider entity ID",
    entryPoint: "Single sign-on URL",
    cert: "Signing certificate",
    domainVerified: "Domain verified",
    domainNotVerified: "Domain not verified",
    verifyDomain: "Verify the domain",
    verifyTitle: "Verify {{domain}}",
    verifyHelp: "Create this TXT record in the DNS zone of the domain, then check it. Nobody can sign in through the provider before.",
    recordName: "Record name",
    recordValue: "Value",
    check: "Check the record",
    verified: "Domain verified: single sign-on is enabled",
    callbackUrl: "Redirect URL to configure in the identity provider:",
    metadata: "Service provider metadata",
    enforce: "Require single sign-on",
    enforceHelp: "Password sign-in is refused for this domain (instance administrators excepted).",
  },
  license: {
    title: "License",
    help: "Releo Community is free. A Releo Enterprise license unlocks multiple organizations, single sign-on, white-label public pages, the audit log and release approvals.",
    community: "Community edition, without license.",
    licensedTo: "Licensed to {{customer}}.",
    seats: "Seats",
    seatsUsed: "{{used}} / {{limit}} used",
    seatsUnlimited: "{{used}} used (unlimited)",
    expiresAt: "Expires on",
    features: "Features",
    install: "Install the license",
    replace: "Replace the license",
    installHelp: "Paste the license key you received. It is verified offline and stored encrypted.",
    key: "License key",
    remove: "Remove",
    fromEnv: "The license is set with the LICENSE_KEY environment variable.",
    requiresEnterprise: "Requires Releo Enterprise",
    bannerGrace: "The Releo Enterprise license has expired. Enterprise features stay enabled until {{date}}: renew it.",
    bannerExpired: "The Releo Enterprise license has expired: Enterprise features are disabled. Your data is kept.",
    bannerInvalid: "The Releo Enterprise license key is invalid.",
    bannerSeats: "{{used}} seats are used but the license covers {{limit}}. Members who only view apps are free.",
    plan: { community: "Community", enterprise: "Enterprise", free: "Free", pro: "Pro", business: "Business" },
    status: { valid: "Valid", grace: "Grace period", expired: "Expired", invalid: "Invalid" },
    feature: { multi_org: "Multiple organizations", sso: "Single sign-on", white_label: "White-label pages", audit_log: "Audit log", audit_export: "Audit log export", approvals: "Release approvals" },
  },
  org: {
    invite: "Invite",
    inviteHelp: "They receive an email to join, with or without an account.",
    invitationSent: "Invitation sent to {{email}}",
    invitationExtended: "Invitation extended for 7 days",
    shareLinkTitle: "Share the invitation link",
    shareLinkHelp: "No email can be sent from this instance: send this link to {{email}}. It is valid for 7 days.",
    invitationLink: "Invitation link",
    pendingInvitations: "Pending invitations",
    pendingInvitationsHelp: "Invitations expire after 7 days. Resend one to extend it.",
    invitationExpires: "Expires",
    invitationExpired: "Expired",
    invitedBy: "Invited by {{name}}",
    copyLink: "Copy link",
    resend: "Resend",
    help: "Apps, store credentials, providers, tags and devices belong to the organization.",
    rename: "Rename",
    new: "New organization",
    newHelp: "You become its owner and switch to it.",
    members: "Members",
    membersHelp: "Owners and administrators manage the organization and every app. Members only see the apps they are added to.",
    addMember: "Add member",
    addMemberHelp: "The person must already have an account on this instance.",
    memberSince: "Member since",
    roleOwner: "Owner",
    roleAdmin: "Administrator",
    roleMember: "Member",
    none: "No organization",
    noneHelp: "You are not a member of any organization. Ask an administrator to add you.",
  },
  admin: {
    users: "Users",
    newUser: "New user",
    admin: "Administrator",
    user: "User",
    disabled: "Disabled",
    enable: "Enable",
    disable: "Disable",
    resetPassword: "Reset password",
    newPassword: "New password",
    signup: "Allow sign-up",
    signupHelp: "When enabled, anyone reaching this instance can create an account. New accounts join the first organization as members, without access to any app.",
    you: "you",
    apple: "App Store Connect API keys",
    appleHelp: "App Store Connect → Users and Access → Integrations → Team keys (App Manager role or above).",
    google: "Google Play service accounts",
    googleHelp: "Google Cloud service account with access granted in Play Console → Users and permissions.",
    keyId: "Key ID",
    issuerId: "Issuer ID",
    keyType: "Key type",
    keyTeam: "Team",
    keyIndividual: "Individual",
    p8: "Private key (.p8)",
    p8Help: "Paste the content or pick the .p8 file. Stored encrypted, never displayed again.",
    serviceAccount: "Service account JSON",
    serviceAccountHelp: "Paste the content or pick the .json key file.",
    replaceSecret: "Leave empty to keep the current secret",
    chooseFile: "Choose file",
    llmTitle: "LLM providers",
    llmHelp: "Any OpenAI-compatible API: OpenRouter, OpenAI, Mistral, Groq, Ollama…",
    baseUrl: "Base URL",
    apiKey: "API key",
    model: "Model",
    temperature: "Temperature",
    gitTitle: "Git providers",
    gitHelp: "Used to list commits between two builds for release notes.",
    type: "Type",
    token: "Token",
    tokenHelp: "Read-only access to repositories. Bitbucket: username:app_password. Azure DevOps: personal access token with the Code (Read) scope.",
    baseUrlHelp: "GitHub Enterprise: https://host/api/v3 · GitLab/Gitea/Forgejo: instance URL · empty for github.com / gitlab.com / bitbucket.org",
    baseUrlHelpAzure: "Organization URL, required: https://dev.azure.com/{organization} (or https://{organization}.visualstudio.com). Repositories are then written project/repo.",
    testOk: "Connection OK",
    testFailed: "Check failed",
    instanceTitle: "Instance",
  },
  keystore: {
    title: "Upload keystore",
    description: "Archive of the keystore used to sign the .aab before uploading it, to set up the project on a new machine. Releo never signs builds.",
    upload: "Store the keystore",
    uploadHelp: "The file and passwords are encrypted at rest. Only app managers and admins can download them.",
    replace: "Replace",
    none: "No keystore stored for this app.",
    file: "Keystore file",
    importProperties: "Fill the fields from an existing key.properties",
    keyPasswordHelp: "Leave empty if identical to storePassword",
    updated: "Updated",
    downloadZip: "Download (.zip)",
    delete: "Delete the stored keystore",
    cli: "From the command line (API key with the keystore:read scope)",
  },
  commitPicker: {
    branch: "Branch",
    commit: "Commit",
    none: "— No commit —",
    manual: "Enter a SHA / reference…",
    pickFromList: "Pick from the branch commits",
  },
  settings: {
    emailUnverified: "Your email address is not confirmed yet: confirm it to sign in with your organization's single sign-on.",
    password: "Password",
    passwordSso: "You sign in through your organization's identity provider: there is no Releo password to change.",
    nameFromIdp: "Managed by your identity provider.",
    title: "My settings",
    profile: "Profile",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    apiKeys: "API keys",
    apiKeysHelp: "Use them from CI: Authorization: Bearer <key>. A key never has more rights than you.",
    newKey: "New API key",
    scopes: "Scopes",
    restrictApps: "Restrict to apps",
    allApps: "All my apps",
    expiresAt: "Expires",
    never: "Never",
    lastUsed: "Last used",
    tokenCreated: "Copy this key now, it won't be shown again:",
    revoke: "Revoke",
    ciExample: "CI example",
  },
  scopes: {
    "apps:read": "Read apps",
    "builds:read": "Read builds",
    "builds:write": "Upload builds",
    "builds:promote": "Promote builds",
    "notes:write": "Edit release notes",
    "keystore:read": "Download the Android keystore",
    "keystore:write": "Store the Android keystore",
    admin: "Administration",
  },
};

export type Messages = typeof en;
